import { Link, useParams, useLoaderData, useNavigate, useLocation } from 'react-router-dom'
import { format } from 'date-fns'
import { ArrowRight, Clock, Calendar, User } from 'lucide-react'
import { type WPPost, type WPCategory } from '../../api/wordpress'
import { BlogSEO } from '../../components/blog/BlogSEO'

interface LoaderData {
  posts: WPPost[]
  totalPages: number
  categories: WPCategory[]
  page: number
}

export function BlogListPage() {
  const { categorySlug } = useParams<{ categorySlug?: string }>()
  const { posts, totalPages, categories, page } = useLoaderData() as LoaderData
  const navigate = useNavigate()
  const location = useLocation()

  const currentPage = page ?? parseInt(new URLSearchParams(location.search).get('page') ?? '1', 10)
  const currentCategory = categorySlug ? categories.find(c => c.slug === categorySlug) : null

  function goToPage(p: number) {
    const params = new URLSearchParams(location.search)
    if (p === 1) params.delete('page')
    else params.set('page', String(p))
    navigate(`${location.pathname}${params.toString() ? '?' + params.toString() : ''}`)
  }

  function getFeaturedImage(post: WPPost): string | null {
    if (post.featured_image_url) return post.featured_image_url
    const media = post._embedded?.['wp:featuredmedia']?.[0]
    return media?.source_url || null
  }

  function getAuthor(post: WPPost) {
    if (post.author_details) return { name: post.author_details.name, avatar: post.author_details.avatar }
    const author = post._embedded?.author?.[0]
    return { name: author?.name || 'Wedsy', avatar: author?.avatar_urls?.['96'] }
  }

  function getCategories(post: WPPost): string[] {
    const terms = post._embedded?.['wp:term']?.[0]
    return terms?.filter(t => t.taxonomy === 'category').map(t => t.name) || []
  }

  function getExcerpt(post: WPPost): string {
    return post.excerpt.rendered.replace(/<[^>]*>/g, '').trim()
  }

  function getReadingTime(post: WPPost): number {
    if (post.reading_time) return post.reading_time
    const content = post.content.rendered.replace(/<[^>]*>/g, '')
    return Math.ceil(content.split(/\s+/).length / 200)
  }

  return (
    <div className="min-h-screen">
      <BlogSEO type="list" />
      {/* Hero */}
      <div className="bg-gradient-to-b from-primary/5 to-transparent py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl text-text-primary mb-3"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontStyle: 'italic' }}
          >
            {currentCategory ? currentCategory.name : 'The Wedsy Blog'}
          </h1>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto">
            {currentCategory
              ? `Articles about ${currentCategory.name.toLowerCase()}`
              : 'Tips, inspiration, and advice for planning your perfect wedding.'
            }
          </p>
        </div>
      </div>

      {/* Posts */}
      <div className="max-w-4xl mx-auto px-4 pb-12 pt-0">
        <div className="space-y-8">
          {posts.map((post, index) => {
            const featuredImage = getFeaturedImage(post)
            const author = getAuthor(post)
            const postCategories = getCategories(post)
            const readingTime = getReadingTime(post)
            const isFirst = index === 0

            if (isFirst && featuredImage) {
              return (
                <article key={post.id} className="group">
                  <Link to={`/blog/${post.slug}`} className="block">
                    <div className="relative aspect-[16/9] md:aspect-[2/1] rounded-2xl overflow-hidden bg-background-elevated mb-6">
                      <img src={featuredImage} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                        {postCategories.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {postCategories.map(cat => (
                              <span key={cat} className="px-3 py-1 text-xs font-medium rounded-full bg-white/20 text-white backdrop-blur-sm">{cat}</span>
                            ))}
                          </div>
                        )}
                        <h2 className="text-2xl md:text-4xl font-bold text-white mb-3 leading-tight" dangerouslySetInnerHTML={{ __html: post.title.rendered }} suppressHydrationWarning />
                        <p className="text-white/80 line-clamp-2 mb-4 max-w-2xl" dangerouslySetInnerHTML={{ __html: getExcerpt(post) }} suppressHydrationWarning />
                        <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
                          <div className="flex items-center gap-2">
                            {author.avatar ? <img src={author.avatar} alt="" className="w-6 h-6 rounded-full" /> : <User className="w-4 h-4" />}
                            <span>{author.name}</span>
                          </div>
                          <span>&middot;</span>
                          <time dateTime={post.date} suppressHydrationWarning>{format(new Date(post.date), 'MMM d, yyyy')}</time>
                          <span>&middot;</span>
                          <div className="flex items-center gap-1"><Clock className="w-4 h-4" /><span>{readingTime} min read</span></div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </article>
              )
            }

            return (
              <article key={post.id} className="group bg-background-card rounded-xl border border-border overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                <Link to={`/blog/${post.slug}`} className="flex flex-col md:flex-row">
                  {featuredImage && (
                    <div className="md:w-72 flex-shrink-0">
                      <div className="aspect-[16/10] md:h-full overflow-hidden bg-background-elevated">
                        <img src={featuredImage} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    </div>
                  )}
                  <div className="flex-1 p-5 md:p-6">
                    {postCategories.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {postCategories.map(cat => (
                          <span key={cat} className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">{cat}</span>
                        ))}
                      </div>
                    )}
                    <h2 className="text-xl font-semibold text-text-primary mb-2 group-hover:text-primary transition-colors leading-snug" dangerouslySetInnerHTML={{ __html: post.title.rendered }} suppressHydrationWarning />
                    <p className="text-text-secondary line-clamp-2 mb-4" dangerouslySetInnerHTML={{ __html: getExcerpt(post) }} suppressHydrationWarning />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-sm text-text-secondary">
                        <div className="flex items-center gap-1.5">
                          {author.avatar ? <img src={author.avatar} alt="" className="w-5 h-5 rounded-full" /> : <User className="w-4 h-4" />}
                          <span className="hidden sm:inline">{author.name}</span>
                        </div>
                        <span className="hidden sm:inline">&middot;</span>
                        <div className="hidden sm:flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /><time dateTime={post.date} suppressHydrationWarning>{format(new Date(post.date), 'MMM d')}</time></div>
                        <span className="hidden sm:inline">&middot;</span>
                        <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /><span>{readingTime} min read</span></div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  </div>
                </Link>
              </article>
            )
          })}
        </div>

        {totalPages > 1 && (
          <nav className="mt-12 flex items-center justify-center gap-2">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="px-5 py-2.5 text-sm font-medium rounded-lg border border-border bg-background-card text-text-primary hover:bg-background-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Previous</button>
            <span className="px-4 py-2 text-sm text-text-secondary">Page {currentPage} of {totalPages}</span>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="px-5 py-2.5 text-sm font-medium rounded-lg border border-border bg-background-card text-text-primary hover:bg-background-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Next</button>
          </nav>
        )}

        {posts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-text-secondary">No posts yet. Check back soon!</p>
          </div>
        )}
      </div>
    </div>
  )
}
